import createClient from 'openapi-fetch';
import type { paths, components } from '../generated/api';

// Re-export schema types for consumers
export type AuthResponse = components['schemas']['AuthResponse'];
export type User = components['schemas']['User'];
export type ApiKey = components['schemas']['ApiKey'];
export type DashboardStats = components['schemas']['DashboardStats'];
export type SystemHealth = components['schemas']['SystemHealth'];
export type Activity = components['schemas']['Activity'];
export type FeedStatsItem = components['schemas']['FeedStatsItem'];
export type FeedHistoryItem = components['schemas']['FeedHistoryItem'];

// Normalized login result consumed by AuthContext (real response is AuthResponse).
export interface LoginResult {
  token: string;
  user: components['schemas']['AdminUser'];
}

// ---------------------------------------------------------------------------
// Mock data (used when REACT_APP_USE_MOCKS=true)
// ---------------------------------------------------------------------------

const mockUsers: User[] = [
  { id: 'user-1', email: 'john@example.com', status: 'active', plan: 'starter', website: 'https://example.com', createdAt: '2024-01-15T10:30:00Z', lastLoginAt: '2024-01-20T14:22:00Z' },
  { id: 'user-2', email: 'jane@demo.com', status: 'pending', plan: 'free', website: 'https://demo.com', createdAt: '2024-01-18T09:15:00Z' },
  // 'pending', not 'inactive': the contract's user-status enum advertised a
  // value users.status has never held, and this mock was the only thing in the
  // repo using it. Narrowing the enum is what surfaced it.
  { id: 'user-3', email: 'bob@test.org', status: 'pending', plan: 'free', website: 'https://test.org', createdAt: '2024-01-12T16:45:00Z', lastLoginAt: '2024-01-19T11:30:00Z' },
  { id: 'user-4', email: 'alice@startup.io', status: 'active', plan: 'publisher', website: 'https://startup.io', createdAt: '2024-01-10T08:20:00Z', lastLoginAt: '2024-01-21T09:45:00Z' },
  { id: 'user-5', email: 'charlie@blog.net', status: 'suspended', plan: 'free', website: 'https://blog.net', createdAt: '2024-01-05T14:15:00Z', lastLoginAt: '2024-01-18T16:20:00Z' },
];

const mockApiKeys: ApiKey[] = [
  { id: 'key-1', domain: 'example.com', key: 'imgaff_live_abc123def456', status: 'active', rateLimit: 1000, usage: 245, createdAt: '2024-01-10T08:15:00Z', lastUsedAt: '2024-01-20T16:45:00Z' },
  { id: 'key-2', domain: 'demo.com', key: 'imgaff_live_xyz789uvw012', status: 'active', rateLimit: 500, usage: 89, createdAt: '2024-01-12T11:30:00Z', lastUsedAt: '2024-01-21T10:15:00Z' },
  { id: 'key-3', domain: 'test.org', key: 'imgaff_live_mno345pqr678', status: 'inactive', rateLimit: 2000, usage: 1456, createdAt: '2024-01-08T14:20:00Z', lastUsedAt: '2024-01-19T15:30:00Z' },
  { id: 'key-4', domain: 'startup.io', key: 'imgaff_live_ghi901jkl234', status: 'active', rateLimit: 1500, usage: 678, createdAt: '2024-01-14T09:10:00Z', lastUsedAt: '2024-01-21T14:25:00Z' },
  { id: 'key-5', domain: 'blog.net', key: 'imgaff_live_stu567vwx890', status: 'suspended', rateLimit: 750, usage: 1200, createdAt: '2024-01-06T12:45:00Z', lastUsedAt: '2024-01-18T18:30:00Z' },
];

const mockActivities: Activity[] = [
  { id: 'activity-1', type: 'user_registered', message: 'New user registered: alice@startup.io', timestamp: '2024-01-21T10:15:00Z', metadata: { userId: 'user-4' } },
  { id: 'activity-2', type: 'api_key_created', message: 'API key created for domain: startup.io', timestamp: '2024-01-21T09:30:00Z', metadata: { keyId: 'key-4', domain: 'startup.io' } },
  { id: 'activity-3', type: 'user_login', message: 'User login: john@example.com', timestamp: '2024-01-21T08:45:00Z', metadata: { userId: 'user-1' } },
  { id: 'activity-4', type: 'system_alert', message: 'High API usage detected for blog.net', timestamp: '2024-01-20T22:15:00Z', metadata: { keyId: 'key-5', usage: 1200 } },
  { id: 'activity-5', type: 'feed_uploaded', message: 'Feed uploaded: electronics-q1.csv (amazon)', timestamp: '2024-01-20T18:40:00Z', metadata: { network: 'amazon', filename: 'electronics-q1.csv' } },
];

// Route mock responses by method + path

// Outreach mock data. Mutable on purpose — see the note on the verify route.
const mockProspects: Array<{
  domain: string; contactAddress: string | null; contactUrl: string | null;
  contactIsRole: boolean; contactVerified: boolean; contactVerifiedBy: string | null;
  networks: string[]; discoverySource: string; segment: string | null;
  isHoldout: boolean; createdAt: string;
}> = [
  {
    domain: 'stylebyemily.example', contactAddress: 'hello@stylebyemily.example',
    contactUrl: 'https://stylebyemily.example/contact/', contactIsRole: true,
    contactVerified: false, contactVerifiedBy: null, networks: ['Awin'],
    discoverySource: 'seed-expansion: linked from https://bloglist.example/links',
    segment: 'english-fashion', isHoldout: false, createdAt: '2026-08-15T09:00:00Z',
  },
  {
    domain: 'thefrugalkitchen.example', contactAddress: 'contact@thefrugalkitchen.example',
    contactUrl: 'https://thefrugalkitchen.example/about/', contactIsRole: true,
    contactVerified: false, contactVerifiedBy: null, networks: [],
    discoverySource: 'seed-expansion: linked from 2 pages, including https://bloglist.example/links',
    segment: 'english-fashion', isHoldout: false, createdAt: '2026-08-15T09:00:00Z',
  },
  {
    domain: 'quietmornings.example', contactAddress: null, contactUrl: null,
    contactIsRole: false, contactVerified: false, contactVerifiedBy: null,
    networks: ['Skimlinks'],
    discoverySource: 'seed-expansion: linked from https://bloglist.example/links',
    segment: 'english-fashion', isHoldout: false, createdAt: '2026-08-15T09:00:00Z',
  },
];

const mockSuppressed: string[] = ['opted-out.example'];

const mockFormPackets = [
  {
    domain: 'stylebyemily.example',
    formUrl: 'https://stylebyemily.example/contact/',
    subject: 'The photos on stylebyemily.example',
    text: [
      'I noticed 412 photos across your site, 88 of them running shoes.',
      '',
      "ImgAff matches the products in a publisher's own catalogue to the images already on their pages, so photos become shoppable without new content.",
      '',
      'If it is useful I can send the full image audit for stylebyemily.example — no account needed.',
      '',
      '— Kris',
      '',
      'We found your site via curated list. If you would rather not hear from me, reply to this and I will not contact you again.',
    ].join('\n'),
  },
  {
    domain: 'quietmornings.example',
    formUrl: 'https://quietmornings.example/work-with-me/',
    subject: 'The photos on quietmornings.example',
    text: [
      'I noticed 168 photos across your site, 40 of them home interiors.',
      '',
      "ImgAff matches the products in a publisher's own catalogue to the images already on their pages, so photos become shoppable without new content.",
      '',
      'If it is useful I can send the full image audit for quietmornings.example — no account needed.',
      '',
      '— Kris',
      '',
      'We found your site via curated list. If you would rather not hear from me, reply to this and I will not contact you again.',
    ].join('\n'),
  },
];

const mockCandidates = [
  { domain: 'instagram.com', verdict: 'excluded', reason: 'instagram.com is never a prospect',
    provider: 'seed-expansion', evidence: 'linked from https://bloglist.example/links', timesSeen: 3,
    firstSeen: '2026-08-15T09:00:00Z' },
  { domain: 'deadblog.example', verdict: 'rejected',
    reason: 'only 4 images across 3 pages — the audit would have little to say',
    provider: 'seed-expansion', evidence: 'linked from https://bloglist.example/links', timesSeen: 1,
    firstSeen: '2026-08-15T09:00:00Z' },
  { domain: 'says-no.example', verdict: 'disallowed',
    reason: 'robots.txt disallows / (robots.txt group "*")', provider: 'seed-expansion',
    evidence: 'linked from https://bloglist.example/links', timesSeen: 1,
    firstSeen: '2026-08-15T09:00:00Z' },
];

const mockRoutes: Record<string, (url: URL, init?: RequestInit) => unknown> = {
  // ---------------------------------------------------------------- outreach
  // A tiny in-memory queue, so ticking an address in mock mode actually moves
  // the row out of the review list — the whole point of the screen is that one
  // transition, and a mock that no-ops it would demo the wrong thing.
  'GET /admin/outreach/summary': () => ({
    prospects: {
      verified: mockProspects.filter(p => p.contactVerified).length,
      awaiting: mockProspects.filter(p => !p.contactVerified && p.contactAddress).length,
      no_address: mockProspects.filter(p => !p.contactAddress).length,
      total: mockProspects.length,
    },
    candidates: { qualified: 2, deprioritized: 1, rejected: 4, excluded: 37, suppressed: 1 },
    suppressed: mockSuppressed.length,
    contacted: 0,
    prepared: 0,
  }),

  'GET /admin/outreach/prospects': (url) => ({
    prospects: mockProspects.filter(p =>
      p.contactVerified === (url.searchParams.get('verified') === 'true')),
  }),

  'POST /admin/outreach/prospects/{domain}/verify': (url, init) => {
    const domain = decodeURIComponent(url.pathname.split('/')[4] || '');
    const body = JSON.parse((init?.body as string) || '{}');
    const prospect = mockProspects.find(p => p.domain === domain);
    if (!prospect) throw new Response(JSON.stringify({ error: `No prospect found for ${domain}.` }), { status: 404 });
    prospect.contactVerified = true;
    prospect.contactAddress = body.address;
    prospect.contactVerifiedBy = 'admin@ezaff.com';
    return { domain, address: body.address, verifiedBy: 'admin@ezaff.com' };
  },

  'POST /admin/outreach/suppress': (_url, init) => {
    const body = JSON.parse((init?.body as string) || '{}');
    mockSuppressed.push(body.domain);
    const i = mockProspects.findIndex(p => p.domain === body.domain);
    if (i >= 0) mockProspects.splice(i, 1);
    return { domain: body.domain, suppressed: true };
  },

  'GET /admin/outreach/candidates': () => ({ candidates: mockCandidates }),

  // The form channel. `mockFormPackets` is deliberately the same shape the real
  // route returns, text and all, so the tab can be driven end to end offline.
  'GET /admin/outreach/form/cohort': (url) => ({
    stage: url.searchParams.get('stage') || 'A',
    items: mockFormPackets,
    skipped: [{
      domain: 'noimages.example',
      reason: 'nothing citable measured — robots.txt refused us, or the site has no images to talk about.',
    }],
  }),

  'POST /admin/outreach/form/approve': (_url, init) => {
    const body = JSON.parse((init?.body as string) || '{}');
    const reviewed = new Set<string>(body.reviewedDomains || []);
    const unread = mockFormPackets.filter(p => !reviewed.has(p.domain)).map(p => p.domain);
    if (unread.length) {
      throw new Response(JSON.stringify({
        error: 'Stage A requires every message to be read before approval.', unread,
      }), { status: 400, headers: { 'content-type': 'application/json' } });
    }
    return { approvalId: 41, stage: body.stage || 'A', approver: 'admin@ezaff.com', items: mockFormPackets.length };
  },

  'POST /admin/outreach/form/prepare': () => ({ packets: mockFormPackets, skipped: [] }),

  'POST /admin/outreach/form/confirm': (_url, init) => {
    const body = JSON.parse((init?.body as string) || '{}');
    return { domain: body.domain, stage: body.stage || 'A', confirmedBy: 'admin@ezaff.com', recorded: 'sent' };
  },

  'POST /admin/outreach/prospects/{domain}/verify-form': (url, init) => {
    const body = JSON.parse((init?.body as string) || '{}');
    const domain = decodeURIComponent(url.pathname.split('/').slice(-2)[0]);
    return { domain, formUrl: body.formUrl, channel: 'form', verifiedBy: 'admin@ezaff.com' };
  },

  'POST /auth/login': (_url, init) => {
    const body = JSON.parse((init?.body as string) || '{}');
    if (body.email === 'admin@ezaff.com' && body.password === 'admin123') {
      // Mirrors the real AuthResponse contract shape: { success, message, user, tokens }
      return {
        success: true,
        message: 'Login successful',
        user: { id: 'admin-1', email: 'admin@ezaff.com', role: 'admin' as const, name: 'Admin User' },
        tokens: {
          accessToken: 'mock-jwt-token-' + Date.now(),
          refreshToken: 'mock-refresh-token-' + Date.now(),
          expiresIn: 86400,
        },
      };
    }
    throw new Response(JSON.stringify({ error: 'INVALID_CREDENTIALS', message: 'Invalid email or password' }), { status: 401 });
  },
  'POST /auth/logout': () => ({ message: 'Logged out successfully' }),
  'GET /admin/users': (url) => {
    const status = url.searchParams.get('status');
    const page = Number(url.searchParams.get('page') || 1);
    const limit = Number(url.searchParams.get('limit') || 10);
    let filtered = mockUsers;
    if (status) filtered = filtered.filter(u => u.status === status);
    const start = (page - 1) * limit;
    return { users: filtered.slice(start, start + limit), pagination: { page, limit, total: filtered.length, totalPages: Math.ceil(filtered.length / limit) } };
  },
  'GET /admin/users/{userId}': (url) => {
    const userId = url.pathname.split('/').pop();
    const user = mockUsers.find(u => u.id === userId);
    if (!user) throw new Response(JSON.stringify({ error: 'NOT_FOUND', message: 'User not found' }), { status: 404 });
    return user;
  },
  'PUT /admin/users/{userId}': (url, init) => {
    const userId = url.pathname.split('/').pop();
    const user = mockUsers.find(u => u.id === userId);
    if (!user) throw new Response(JSON.stringify({ error: 'NOT_FOUND', message: 'User not found' }), { status: 404 });
    const updates = JSON.parse((init?.body as string) || '{}');
    return { ...user, ...updates };
  },
  'POST /admin/users/{userId}/approve': (url) => {
    // /admin/users/user-2/approve → segment before the trailing 'approve'
    const segments = url.pathname.split('/');
    const userId = segments[segments.length - 2];
    const user = mockUsers.find(u => u.id === userId);
    if (!user) throw new Response(JSON.stringify({ error: 'NOT_FOUND', message: 'User not found' }), { status: 404 });
    return { ...user, status: 'active' };
  },
  'GET /admin/api-keys': (url) => {
    const page = Number(url.searchParams.get('page') || 1);
    const limit = Number(url.searchParams.get('limit') || 10);
    const start = (page - 1) * limit;
    return { apiKeys: mockApiKeys.slice(start, start + limit), pagination: { page, limit, total: mockApiKeys.length, totalPages: Math.ceil(mockApiKeys.length / limit) } };
  },
  'POST /admin/api-keys': (_url, init) => {
    const request = JSON.parse((init?.body as string) || '{}');
    return { id: 'key-' + Date.now(), domain: request.domain, key: 'imgaff_live_' + Math.random().toString(36).substring(2, 18), status: 'active' as const, rateLimit: request.rateLimit || 1000, usage: 0, createdAt: new Date().toISOString() };
  },
  'PUT /admin/api-keys/{keyId}': (url, init) => {
    const keyId = url.pathname.split('/').pop();
    const key = mockApiKeys.find(k => k.id === keyId);
    if (!key) throw new Response(JSON.stringify({ error: 'NOT_FOUND', message: 'API key not found' }), { status: 404 });
    const updates = JSON.parse((init?.body as string) || '{}');
    return { ...key, ...updates };
  },
  'DELETE /admin/api-keys/{keyId}': () => null,
  'GET /admin/health': () => ({
    overall: 'healthy' as const,
    services: [
      { name: 'API Service', status: 'healthy' as const, responseTime: 45.2, lastCheck: new Date().toISOString(), details: 'All endpoints responding normally' },
      { name: 'Database', status: 'healthy' as const, responseTime: 12.1, lastCheck: new Date().toISOString(), details: 'Connection pool healthy' },
      { name: 'Redis Cache', status: 'not_configured' as const, responseTime: 0, lastCheck: new Date().toISOString(), details: 'Redis is not configured' },
    ],
    metrics: { uptime: '15d 4h 32m', responseTime: 42.8, lastCheck: new Date().toISOString() },
  }),
  'GET /admin/stats': () => ({
    totalUsers: mockUsers.length,
    totalApiKeys: mockApiKeys.length,
    activeUsers: mockUsers.filter(u => u.status === 'active').length,
    systemHealth: 'healthy' as const,
  }),
  // Mock deliberately returns a suggestion that TRIPS a warning, so the review
  // path is what a developer sees by default rather than the happy one.
  'POST /admin/audit/prospect/opening': () => ({
    success: true,
    line: 'You have 48 photos on the site, 28 of them showing hair care, and no way for a reader to shop what is in them — worth a look at your traffic.',
    warnings: ['Says "traffic" — we have no data behind that. Remove it before sending.'],
  }),
  'POST /admin/audit/prospect': (_url, init) => {
    const body = JSON.parse((init?.body as string) || '{}');
    let host = 'blog.example';
    try { host = new URL(body.url).hostname; } catch { /* mock only */ }
    const bestPage = `https://${host}/2026/long-hair/`;
    return {
      success: true,
      readable: true,
      followUp: {
        subject: 'Your hair care photos — the breakdown',
        body: 'Hi [first name],\n\nThanks — here\'s what I found across the 4 pages I read.\n\n• Long hair, don\'t care — 30 photos\n\n— Kris',
        mailtoUrl: 'mailto:?subject=breakdown',
      },
      report: {
        entryUrl: body.url,
        host,
        pagesRead: 4,
        pagesAttempted: 5,
        totals: { images: 48, missingAlt: 6, linkedToAttachmentPage: 43 },
        subjects: [
          { subject: 'Hair care and styling', images: 28 },
          { subject: 'Styling tools', images: 5 },
        ],
        remainder: 6,
        topics: ['hair', 'styling'],
        bestPage,
        pages: [
          { url: body.url, readable: false, reason: 'We opened that page but found no images on it.' },
          { url: bestPage, readable: true, title: 'Long hair', images: 28, missingAlt: 3, linkedToAttachmentPage: 25 },
          { url: `https://${host}/2026/bob/`, readable: true, title: 'Bob cuts', images: 12, missingAlt: 2, linkedToAttachmentPage: 10 },
          { url: `https://${host}/2026/colour/`, readable: true, title: 'Colour', images: 8, missingAlt: 1, linkedToAttachmentPage: 8 },
        ],
      },
      draft: {
        subject: `Your hair care and styling photos on ${host}`,
        body: `Hi,\n\nI was reading ${bestPage} and noticed something about how the images on it are set up.\n\nAcross the 4 pages I looked at, 43 images link to WordPress attachment pages — thin pages that take the reader off the post.\n\nWould it be useful if I sent over what I found on your pages? No obligation either way.\n\n— Kris\nhello@imgaff.com`,
        mailtoUrl: 'mailto:?subject=example&body=example',
        inviteUrl: `https://imgaff.com/audit.html?url=${encodeURIComponent(bestPage)}`,
        notes: {
          host,
          pagesRead: 4,
          bestPage,
          subjects: ['Hair care and styling', 'Styling tools'],
          citableFindings: ['43 images across 4 pages link to WordPress attachment pages — thin pages that take the reader off the post'],
        },
      },
    };
  },
  'GET /admin/audit-funnel': () => ({
    days: 30,
    hops: {
      audited:                 { outreach: 14, home: 0, organic: 6, other: 0 },
      unreadable:              { outreach: 2,  home: 0, organic: 1, other: 0 },
      report_stored_new:       { outreach: 4,  home: 0, organic: 1, other: 0 },
      report_stored_existing:  { outreach: 1,  home: 0, organic: 0, other: 0 },
      link_redeemed:           { outreach: 0,  home: 0, organic: 0, other: 3 },
    },
    daily: [
      { day: '2026-08-17', source: 'outreach', metric: 'audited', n: 9 },
      { day: '2026-08-18', source: 'outreach', metric: 'audited', n: 5 },
    ],
    continuation: {
      accountsCreated: 4, siteVerified: 2, accountApproved: 1,
      apiKeyIssued: 1, feedConnected: 1, widgetFirstRequest: 1, firstClick: 0,
    },
  }),
  'GET /admin/acquisition': () => ({
    days: 90,
    signupsBySource: [
      { source: 'wordpress-org', medium: '', count: 4 },
      { source: 'case-study', medium: 'referral', count: 2 },
      { source: 'direct/unknown', medium: '', count: 2 },
    ],
    leadsBySource: [
      { source: 'reddit', medium: 'social', count: 3 },
      { source: 'direct/unknown', medium: '', count: 1 },
    ],
    recentSignups: [
      { email: 'blogger@example.com', created_at: '2026-07-15T10:00:00Z', attribution: { utm_source: 'wordpress-org', landing_page: '/' } },
      { email: 'niche@example.com', created_at: '2026-07-12T08:30:00Z', attribution: null },
    ],
    recentLeads: [
      { email: 'reader@example.com', source: 'homepage', created_at: '2026-07-16T12:00:00Z', attribution: { utm_source: 'reddit', utm_medium: 'social' } },
    ],
  }),
  'GET /admin/activity': (url) => {
    const limit = Number(url.searchParams.get('limit') || 10);
    return { activities: mockActivities.slice(0, limit) };
  },
  // Fourteen days of plausible traffic, shaped like the real thing: mostly
  // memory and fresh, a stale tail, a few misses, and fills that all terminate.
  // serve_memory only exists from day 7 on, so the "—" rendering for days that
  // predate a metric is exercised in mock mode instead of only in production.
  'GET /admin/match-ops': (url: URL) => {
    const days = Number(url.searchParams.get('days')) || 14;
    const series: Array<{ day: string; metric: string; count: number }> = [];
    const start = Date.UTC(2026, 6, 20);
    let memorySince: string | null = null;
    for (let i = 0; i < days; i++) {
      const day = new Date(start + i * 86400000).toISOString().slice(0, 10);
      const miss = 40 + ((i * 7) % 25);
      const stale = 30 + ((i * 5) % 20);
      const fresh = 90 + ((i * 11) % 40);
      const queued = miss + stale;
      const push = (metric: string, count: number) => series.push({ day, metric, count });
      if (i >= 6) {
        if (!memorySince) memorySince = day;
        push('serve_memory', 120 + ((i * 13) % 60));
      }
      push('serve_fresh', fresh);
      push('serve_stale', stale);
      push('serve_miss', miss);
      push('fill_queued', queued);
      // Terminals add up to queued, because that is the invariant the view exists
      // to display; a mock that does not add up teaches the wrong thing.
      const blocked = Math.round(queued * 0.12);
      const skipped = Math.round(queued * 0.2);
      const failed = i === 9 ? 4 : 0;
      push('fill_completed', queued - blocked - skipped - failed);
      push('fill_blocked', blocked);
      push('fill_skipped_fresh', skipped);
      if (failed) push('fill_failed', failed);
    }
    return {
      success: true,
      days,
      metrics: [
        'serve_memory', 'serve_fresh', 'serve_stale', 'serve_miss', 'serve_legacy',
        'fill_queued', 'fill_deduplicated', 'fill_shed',
        'fill_completed', 'fill_failed', 'fill_skipped_fresh', 'fill_blocked',
      ],
      series,
      since: {
        serve_memory: memorySince,
        fill_skipped_fresh: memorySince,
        fill_blocked: memorySince,
      },
    };
  },
  // upload_runs deliberately far exceeds feed_count, and one network has feeds
  // with no runs at all — the two shapes that the old query collapsed.
  'GET /admin/feeds/stats': () => ({
    success: true,
    stats: [
      { network: '2performant', feed_count: 5, publishers: 1, total_products: 21599, upload_runs: 38, avg_processing_time: 2000, last_upload: '2026-08-01T21:00:00Z' },
      { network: 'amazon', feed_count: 1, publishers: 1, total_products: 1200, upload_runs: 4, avg_processing_time: 3100, last_upload: '2026-04-05T14:30:00Z' },
      { network: 'demo', feed_count: 1, publishers: 1, total_products: 38, upload_runs: 0, avg_processing_time: null, last_upload: '2026-07-12T21:00:00Z' },
    ],
  }),
  'GET /admin/feeds/history': () => ({
    success: true,
    history: [
      { network: 'amazon', filename: 'electronics-q1.csv', total_products: 250, processed_products: 245, skipped_products: 5, processing_time: 3200, success_rate: 98.0, created_at: '2026-04-05T14:30:00Z' },
      { network: 'awin', filename: 'outdoor-gear.csv', total_products: 180, processed_products: 178, skipped_products: 2, processing_time: 2100, success_rate: 98.89, created_at: '2026-04-03T09:15:00Z' },
      { network: 'shareasale', filename: 'home-garden-spring.csv', total_products: 95, processed_products: 95, skipped_products: 0, processing_time: 800, success_rate: 100.0, created_at: '2026-03-28T16:45:00Z' },
    ],
  }),
  // Shaped like the real registry (services/feature-flags.ts) so mock mode
  // exercises all three states rather than just 'beta'.
  'GET /admin/flags': () => ({
    success: true,
    flags: [
      { key: 'overlay-discounts', state: 'beta', beta: [30], since: '2026-08-23', ageDays: 3, note: 'overlay-promotion-presentation: shows the discount badge. Delete when released GA or abandoned.' },
      { key: 'overlay-tooltip-position', state: 'beta', beta: [30], since: '2026-08-23', ageDays: 3, note: 'Tooltip title placement. Delete once the portal has a real overlay-settings UI to replace it.' },
      { key: 'overlay-bold-style', state: 'beta', beta: [30], since: '2026-08-25', ageDays: 1, note: 'Bold overlay presentation. Delete when released GA or the classic style is retired.' },
      { key: 'tooltip-buy-cta', state: 'beta', beta: [30], since: '2026-08-25', ageDays: 1, note: 'Tooltip "Buy ›" CTA chip. Delete when released GA or the hypothesis is settled.' },
    ],
  }),
  // Shaped like a real sweep of the pilot account: a measured 2Performant shop,
  // one the league does not rank (7 of the live 45 are not in the competition),
  // and one whose network publishes no EPC at all — the three cases the panel
  // has to render differently.
  'GET /admin/network-signals': () => ({
    success: true,
    coverage: [
      { network: '2performant', programmes: 3, withEpc: 2, oldestObservedAt: '2026-09-05T04:00:00Z', newestObservedAt: '2026-09-05T04:00:00Z' },
    ],
    signals: [
      { network: '2performant', programmeKey: '8036', name: 'nailsup.ro', advertiserHost: 'nailsup.ro', programmeStatus: 'active', balanceIndicator: 'green', funded: true, downtimePct: 0, epcValue: 37.11, epcCurrency: 'EUR', epcKind: 'raw', epcRank: 41, epcRankOf: 443, conversionRatePct: 4.9, epcWindowId: '109', epcWindowEnd: '2026-08-30T23:59:59Z', approvalCountPct: 98, approvalAmountPct: 98.2, commissionMin: 15, commissionKind: 'percent', cookieDays: 30, paymentDays: 28, networkScore: 112, networkScoreOf: null, networkScoreKind: '2Performant general rank', observedAt: '2026-09-05T04:00:00Z', ageDays: 0.2, score: 0.81, confidence: 0.8, sortKey: 0.748, reasons: ['EPC at 91% of its network, shrunk for click volume at 62%.', 'Approves 98.2% of commissions by value.'] },
      { network: '2performant', programmeKey: '9899', name: 'store.irinareisler.ro', advertiserHost: 'store.irinareisler.ro', programmeStatus: 'active', balanceIndicator: 'red', funded: false, downtimePct: 12, epcValue: 30.09, epcCurrency: 'EUR', epcKind: 'raw', epcRank: 55, epcRankOf: 443, conversionRatePct: 8.48, epcWindowId: '109', epcWindowEnd: '2026-08-30T23:59:59Z', approvalCountPct: 96, approvalAmountPct: 95.56, commissionMin: 10, commissionKind: 'percent', cookieDays: 30, paymentDays: 26, networkScore: 131, networkScoreOf: null, networkScoreKind: '2Performant general rank', observedAt: '2026-09-05T04:00:00Z', ageDays: 0.2, score: 0.33, confidence: 0.8, sortKey: 0.364, reasons: ['EPC at 88% of its network.', 'Out of prepaid budget, so a share of clicks earn nothing.'] },
      { network: '2performant', programmeKey: '1212', name: 'dehaine.ro', advertiserHost: 'dehaine.ro', programmeStatus: 'active', balanceIndicator: 'green', funded: true, downtimePct: 0, epcValue: null, epcCurrency: null, epcKind: null, epcRank: null, epcRankOf: null, conversionRatePct: null, epcWindowId: null, epcWindowEnd: null, approvalCountPct: 88, approvalAmountPct: 90, commissionMin: 8, commissionKind: 'percent', cookieDays: 30, paymentDays: 30, networkScore: null, networkScoreOf: null, networkScoreKind: null, observedAt: '2026-09-05T04:00:00Z', ageDays: 0.2, score: 0.7, confidence: 0.4, sortKey: 0.58, reasons: ['No EPC reported by this network.', 'Approves 90% of commissions by value.'] },
    ],
  }),
};

function matchRoute(method: string, pathname: string): string | undefined {
  const exact = `${method} ${pathname}`;
  if (mockRoutes[exact]) return exact;
  // Try parameterised routes: /admin/users/user-1 → /admin/users/{userId}
  for (const key of Object.keys(mockRoutes)) {
    const [m, pattern] = key.split(' ', 2);
    if (m !== method) continue;
    const re = new RegExp('^' + pattern.replace(/\{[^}]+\}/g, '[^/]+') + '$');
    if (re.test(pathname)) return key;
  }
  return undefined;
}

const mockFetch: typeof fetch = async (input, init) => {
  // openapi-fetch calls fetch(request) with a fully-built Request and no init,
  // so method and body must be read off the Request when init is absent
  const request = input instanceof Request ? input : null;
  const url = new URL(request ? request.url : String(input));
  const method = (init?.method || request?.method || 'GET').toUpperCase();
  // Strip baseUrl prefix to get the spec path
  const pathname = url.pathname.replace(/^\/api/, '');
  const routeKey = matchRoute(method, pathname);
  if (!routeKey) {
    return new Response(JSON.stringify({ error: 'NOT_FOUND', message: `No mock for ${method} ${pathname}` }), { status: 404, headers: { 'content-type': 'application/json' } });
  }
  let handlerInit = init;
  if (!handlerInit?.body && request && method !== 'GET' && method !== 'HEAD') {
    const contentType = request.headers.get('content-type') || '';
    handlerInit = {
      method,
      body: contentType.includes('multipart/form-data')
        ? ((await request.formData()) as unknown as BodyInit)
        : await request.text(),
    };
  }
  try {
    const data = mockRoutes[routeKey](url, handlerInit);
    if (data === null) return new Response(null, { status: 204 });
    return new Response(JSON.stringify(data), { status: 200, headers: { 'content-type': 'application/json' } });
  } catch (err) {
    if (err instanceof Response) return err;
    throw err;
  }
};

// ---------------------------------------------------------------------------
// Create the openapi-fetch client
// ---------------------------------------------------------------------------

const useMocks = process.env.REACT_APP_USE_MOCKS === 'true';
const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:3000') + '/api';

// Real-mode fetch wrapper: on an explicit 401 HTTP response (never on network
// errors, which throw before a response exists), notify AuthContext — the
// single owner of stored auth state — so it can clear it and log the user out.
const realFetch: typeof fetch = async (input, init) => {
  const response = await fetch(input, init);
  if (response.status === 401) {
    window.dispatchEvent(new Event('ezaff:unauthorized'));
  }
  return response;
};

const client = createClient<paths>({
  baseUrl: apiUrl,
  fetch: useMocks ? mockFetch : realFetch,
});

// ---------------------------------------------------------------------------
// Wrapper: same method surface components already consume
// ---------------------------------------------------------------------------

function getToken(): string | null {
  return localStorage.getItem('accessToken');
}

function authHeaders(): HeadersInit {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const apiService = {
  async login(email: string, password: string): Promise<LoginResult> {
    const { data, error } = await client.POST('/auth/login', { body: { email, password } });
    if (error) throw new Error((error as any).message || 'Login failed');
    // Normalize: the API returns { tokens: { accessToken, ... }, user } while
    // components expect { token, user }. The backend may serialize user.id as a
    // number; coerce to the contract's string type defensively.
    const user = data!.user;
    return { token: data!.tokens.accessToken, user: { ...user, id: String(user.id) } };
  },

  async logout() {
    const { data } = await client.POST('/auth/logout', { headers: authHeaders() });
    return data ?? { message: 'Logged out successfully' };
  },

  async getDashboardStats() {
    const { data, error } = await client.GET('/admin/stats', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to fetch stats');
    return data!;
  },

  async getSystemHealth() {
    const { data, error } = await client.GET('/admin/health', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to fetch health');
    return data!;
  },

  // The audit tool's conversion funnel: daily aggregate counters (no URLs, no
  // IPs) plus the milestone continuation for audit-born accounts. Answers
  // "how many ran the audit, and how many converted" — per source.
  async getAuditFunnel(days?: number) {
    const { data, error } = await client.GET('/admin/audit-funnel', {
      params: { query: days ? { days } : undefined },
      headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Failed to fetch audit funnel');
    return data!;
  },

  async getAcquisition(days?: number) {
    const { data, error } = await client.GET('/admin/acquisition', {
      params: { query: days ? { days } : undefined },
      headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Failed to fetch acquisition');
    return data!;
  },

  // Ask for an alternative opening sentence. Operator-pressed, never automatic:
  // an audit stays free and deterministic, and this is only bought when someone
  // wants a different wording. Returns the sentence WITH any warnings — the
  // operator reviews it, so nothing is withheld from them.
  async suggestProspectOpening(facts: {
    host: string; pagesRead: number; images: number;
    bestPage?: string; topSubject?: string; topSubjectImages?: number;
  }): Promise<{ line: string; warnings: string[] }> {
    const { data, error } = await client.POST('/admin/audit/prospect/opening', {
      body: facts as any,
      headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Could not get an alternative opening');
    return { line: (data as any).line, warnings: (data as any).warnings || [] };
  },

  // Audit a prospect's site and get an editable outreach draft back.
  // Nothing is stored and nothing is sent — see the route's own comment.
  // ------------------------------------------------------------------ outreach
  async getOutreachSummary() {
    const { data, error } = await client.GET('/admin/outreach/summary', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to load outreach summary');
    return data!;
  },

  async getOutreachProspects(params?: { verified?: 'true' | 'false'; limit?: number }) {
    const { data, error } = await client.GET('/admin/outreach/prospects', {
      params: { query: params }, headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Failed to load prospects');
    return data!;
  },

  async verifyOutreachContact(domain: string, address: string) {
    const { data, error } = await client.POST('/admin/outreach/prospects/{domain}/verify', {
      params: { path: { domain } }, body: { address }, headers: authHeaders(),
    });
    if (error) throw new Error((error as any).error || (error as any).message || 'Could not confirm that address');
    return data!;
  },

  async suppressOutreachDomain(domain: string, note?: string) {
    const { data, error } = await client.POST('/admin/outreach/suppress', {
      body: { domain, ...(note ? { note } : {}) }, headers: authHeaders(),
    });
    if (error) throw new Error((error as any).error || (error as any).message || 'Could not suppress that domain');
    return data!;
  },

  async previewFormCohort(params?: { stage?: string; segment?: string }) {
    const { data, error } = await client.GET('/admin/outreach/form/cohort', {
      params: { query: params }, headers: authHeaders(),
    });
    if (error) throw new Error((error as any).error || (error as any).message || 'Failed to load the form cohort');
    return data!;
  },

  async approveFormCohort(reviewedDomains: string[], params?: { stage?: string; segment?: string }) {
    const { data, error } = await client.POST('/admin/outreach/form/approve', {
      body: { reviewedDomains, ...params }, headers: authHeaders(),
    });
    if (error) {
      const unread = (error as any).unread as string[] | undefined;
      throw new Error(((error as any).error || 'Could not approve') +
        (unread?.length ? ` Unread: ${unread.join(', ')}` : ''));
    }
    return data!;
  },

  async prepareFormPackets(approvalId: number, params?: { stage?: string; segment?: string }) {
    const { data, error } = await client.POST('/admin/outreach/form/prepare', {
      body: { approvalId, ...params }, headers: authHeaders(),
    });
    if (error) throw new Error((error as any).error || (error as any).message || 'Could not prepare packets');
    return data!;
  },

  async confirmFormSubmission(domain: string, stage: string, note?: string) {
    const { data, error } = await client.POST('/admin/outreach/form/confirm', {
      body: { domain, stage, ...(note ? { note } : {}) }, headers: authHeaders(),
    });
    if (error) throw new Error((error as any).error || (error as any).message || 'Could not record that submission');
    return data!;
  },

  async verifyOutreachFormUrl(domain: string, formUrl: string) {
    const { data, error } = await client.POST('/admin/outreach/prospects/{domain}/verify-form', {
      params: { path: { domain } }, body: { formUrl }, headers: authHeaders(),
    });
    if (error) throw new Error((error as any).error || (error as any).message || 'Could not confirm that form URL');
    return data!;
  },

  async getOutreachCandidates(params?: { verdict?: string; limit?: number }) {
    const { data, error } = await client.GET('/admin/outreach/candidates', {
      params: { query: params }, headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Failed to load candidates');
    return data!;
  },

  async auditProspect(url: string, pages?: number) {
    const { data, error } = await client.POST('/admin/audit/prospect', {
      body: { url, ...(pages ? { pages } : {}) },
      headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Prospect audit failed');
    return data!;
  },

  async getRecentActivity(params?: { limit?: number }) {
    const { data, error } = await client.GET('/admin/activity', { params: { query: params }, headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to fetch activity');
    return data!;
  },

  async getUsers(params?: { page?: number; limit?: number; status?: string }) {
    const { data, error } = await client.GET('/admin/users', {
      params: { query: params as any },
      headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Failed to fetch users');
    return data!;
  },

  async getUserById(userId: string) {
    const { data, error } = await client.GET('/admin/users/{userId}', { params: { path: { userId } }, headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'User not found');
    return data!;
  },

  async updateUser(userId: string, updates: components['schemas']['UserUpdateRequest']) {
    const { data, error } = await client.PUT('/admin/users/{userId}', { params: { path: { userId } }, body: updates, headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to update user');
    return data!;
  },

  // "Approve website": transactional verification + activation for a pending
  // account (distinct from updateUser status flips, which touch status only)
  async approveUser(userId: string) {
    const { data, error } = await client.POST('/admin/users/{userId}/approve', { params: { path: { userId } }, headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to approve user');
    return data!;
  },

  async getApiKeys(params?: { page?: number; limit?: number }) {
    const { data, error } = await client.GET('/admin/api-keys', { params: { query: params }, headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to fetch API keys');
    return data!;
  },

  async createApiKey(request: components['schemas']['ApiKeyCreateRequest']) {
    const { data, error } = await client.POST('/admin/api-keys', { body: request, headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to create API key');
    return data!;
  },

  async updateApiKey(keyId: string, updates: components['schemas']['ApiKeyUpdateRequest']) {
    const { data, error } = await client.PUT('/admin/api-keys/{keyId}', { params: { path: { keyId } }, body: updates, headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to update API key');
    return data!;
  },

  async deleteApiKey(keyId: string) {
    const { error } = await client.DELETE('/admin/api-keys/{keyId}', { params: { path: { keyId } }, headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to delete API key');
  },

  async getMatchOps(days?: number) {
    const { data, error } = await client.GET('/admin/match-ops', {
      params: { query: days ? { days } : undefined },
      headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Failed to load matching counters');
    return data!;
  },

  async getFlags() {
    const { data, error } = await client.GET('/admin/flags', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to load feature flags');
    return data!;
  },

  async getNetworkSignals() {
    const { data, error } = await client.GET('/admin/network-signals', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to load network signals');
    return data!;
  },

  async getFeedStats() {
    const { data, error } = await client.GET('/admin/feeds/stats', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to fetch feed stats');
    return data!;
  },

  async getFeedHistory(params?: { limit?: number; offset?: number }) {
    const { data, error } = await client.GET('/admin/feeds/history', { params: { query: params }, headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to fetch feed history');
    return data!;
  },
};
